<template>
  <div class="st-page">
    <div class="ui container">
      <div class="st-title">
        <p>注文確認</p>
      </div>
    </div>

    <fetch-loader>
      <checkout-item-placeholder />
      <template v-if="cartItems.length > 0 && order">
        <!-- item list -->
        <checkout-items :order-sale-type="orderSaleType" />

        <!-- payment method -->
        <div class="st-pad-30"></div>
        <selected-payment-method />

        <!-- receive method -->
        <div class="st-pad-10"></div>
        <selected-receiving-method />

        <!-- price infos -->
        <div class="st-pad-20"></div>
        <div class="ui container st-order-info">
          <div class="st-info-line">
            <div class="st-label">
              <div>オーダー番号</div>
            </div>
            <div class="st-value">
              <div>{{order.order_code}}</div>
            </div>
          </div>

          <!-- ご注文点数, 商品合計, 配送料-->
          <div class="st-pad-20"></div>
          <checkout-price-details />

          <div class="st-info-line" v-if="alreadyPaymentOnlyPoint > 0">
            <div class="st-label">
              <div>ポイント利用</div>
            </div>
            <div class="st-value">
              <div>-{{alreadyPaymentOnlyPoint | currency('')}} pt</div>
            </div>
          </div>

          <div class="st-info-line" v-if="alreadyPaymentOnlyCoupon > 0">
            <div class="st-label">
              <div>クーポン利用</div>
            </div>
            <div class="st-value">
              <div>-{{alreadyPaymentOnlyCoupon | currency('')}} 円</div>
            </div>
          </div>

          <div class="st-info-line" v-if="isDepositSaleType">
            <div class="st-label">
              <div>ご予約金額</div>
            </div>
            <div class="st-value">
              <div>-{{alreadyPaymentCreditCard | currency}}&nbsp;<span>(税込)</span></div>
            </div>
          </div>
        </div>

        <div class="ui container st-order-info">
          <hr style="margin: 20px 0;" />

          <div class="st-info-line">
            <div class="st-label">
              <div>お支払い金額</div>
            </div>
            <div class="st-value">
              <div style="font-size: 1.3rem;">
                {{ totalOrderPrice | currency }}&nbsp;<span>(税込)</span>
              </div>
            </div>
          </div>
        </div>

        <checkout-confirm
          :message-type="messageType"
          :message-title="messageTitle"
          :messages="messages"
          :disabled="!isOrderable"
          @submit="handleSubmit"
        />
      </template>
      <template v-else-if="!$store.state.common.initFetching">
        <checkout-complete
          :message-type="messageType"
          :message-title="messageTitle"
          :messages="messages"
          empty-message="購入商品がありません。"
        />
      </template>
    </fetch-loader>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import notifyError from '../../utils/common/notifyError'
import { InitWebviewTitle } from '../../services/initApp'
// template
import CheckoutItems from '../../components/templates/CheckoutItems'
import CheckoutPriceDetails from '../../components/templates/CheckoutPriceDetails'
import CheckoutConfirm from '../../components/templates/CheckoutConfirm'
import CheckoutComplete from '../../components/templates/CheckoutComplete'
import SelectedPaymentMethod from '../../components/templates/checkout/SelectedPaymentMethod'
import SelectedReceivingMethod from '../../components/templates/checkout/SelectedReceivingMethod'
import { getOrderToken } from '../../utils/common/storageHelper'
import { CART_SALE_TYPES, ORDER_SALE_TYPES, PAYMENT_METHODS } from '../../config/constant-types'
import CheckoutItemPlaceholder from '../../components/templates/CheckoutItemPlaceholder'

const TITLE = '決済/お渡し方法'

export default {
  components: {
    CheckoutItemPlaceholder,
    CheckoutItems,
    CheckoutPriceDetails,
    CheckoutConfirm,
    CheckoutComplete,
    SelectedPaymentMethod,
    SelectedReceivingMethod
  },
  metaInfo: {
    title: TITLE
  },
  data () {
    return {
      cartSaleType: CART_SALE_TYPES.CONFIRM,
      messageType: 'error',
      messageTitle: '決済処理に失敗しました',
      messages: [],
      isOrderComplete: false
    }
  },
  /**
   * 客注の場合はURLがメッセージ画面に見えるので、app_nameパラメーターがない状態
   */
  created () {
    InitWebviewTitle(TITLE)
    this.saveOrigin(this.$router.currentRoute.name) // save by router name
    this.initComponentFetch()
  },
  computed: {
    ...mapState({
      order: state => state.cart.remote.order,
      cartItems: state => state.cart.remote.cartItems, // 購入商品
      creditCard: state => state.order.credit_card,
      orderToken: state => state.order.token,
      savedReceiveType: state => state.delivery.input.receiveType,
      savedReceiveMethod: state => state.delivery.input.receiveMethod
    }),
    ...mapGetters([
      'totalOrderPrice',
      'alreadyPayment',
      'isReceiveSelected',
      'getCardPaymentMethodInfo',
      'getDeliveryInfo',
      'isAllowedSelectedPaymentMethod'
    ]),
    orderSaleType () {
      if (this.order && this.order.sale_type) {
        return this.order.sale_type
      } else {
        return ORDER_SALE_TYPES.NONE
      }
    },
    isOrderable () {
      return this.isAllowedSelectedPaymentMethod &&
        this.isReceiveSelected &&
        !this.isOrderComplete &&
        !this.$store.state.common.fetching &&
        this.totalOrderPrice >= 0
    },
    isDepositSaleType () {
      return this.orderSaleType === 'DEPOSIT'
    },
    /* total 支払い済み額 - クレジット決済金額 */
    alreadyPaymentCreditCard () {
      return this.alreadyPayment(payment => {
        return [PAYMENT_METHODS.credit].includes(payment.pay_method)
      })
    },
    /* total 支払い済み額 - ポイント（全種類） */
    alreadyPaymentOnlyPoint () {
      return this.alreadyPayment(payment => {
        return [PAYMENT_METHODS.pre_point, PAYMENT_METHODS.point].includes(payment.pay_method)
      })
    },
    alreadyPaymentOnlyCoupon () {
      return this.alreadyPayment(payment => {
        return [PAYMENT_METHODS.coupon].includes(payment.pay_method)
      })
    }
  },
  methods: {
    ...mapActions([
      'endInitFetch',
      'fetching',
      'saveOrigin',
      'fetchOrderForPayment',
      'authAndSaveUser',
      'fetchCheckoutCartItemCalc',
      'confirmOrder'
    ]),
    async initComponentFetch () {
      try {
        // auth & order
        const payload = await this.fetchOrderForPayment(getOrderToken())
        await this.authAndSaveUser(payload.user_token)
        // cart items
        if (this.cartItems.length === 0) {
          const params = {
            cart_sale_type: this.cartSaleType
          }
          await this.fetchCheckoutCartItemCalc(params)
        }
        this.endInitFetch()
      } catch (e) {
        this.endInitFetch()
        this.messageType = 'warning'
        this.messageTitle = 'エラー'
        if (e.payload && e.payload.messages) {
          this.messages = e.payload.messages
        } else {
          notifyError(e)
          this.messages = [this.$t('errors.common')]
        }
      }
    },
    handleSubmit () {
      const params = {
        order_token: this.orderToken,
        card_seq: this.creditCard.card_seq,
        card_payment: this.getCardPaymentMethodInfo,
        delivery: this.getDeliveryInfo,
        receive_type: this.savedReceiveType,
        receive_method: this.savedReceiveMethod
      }

      this.fetching(() => {
        this.messages = [] // message reset
        return this.confirmOrder(params).then(() => {
          this._processAfterSubmit()
        }).catch(e => {
          if (e.payload && e.payload.messages) {
            this.messages = e.payload.messages
          } else {
            notifyError(e)
            this.messages = [this.$t('errors.common')]
          }
        })
      })
    },
    _processAfterSubmit () {
      const userMessage = '決済に成功しました'
      this.isOrderComplete = true
      this.messageType = 'normal'
      this.messageTitle = userMessage
      this.messages = [this.$t('messages.close')]
      this.postAppMessage(userMessage)
    },
    postAppMessage (userMessage) {
      const appPostMessage = {
        result: 'success',
        user_message: userMessage
      }
      try {
        if (window.webkit) {
          window.webkit.messageHandlers.didFinish.postMessage(
            JSON.stringify(appPostMessage)
          )
        } else if (window.appInterface) {
          window.appInterface.didFinish(JSON.stringify(appPostMessage))
        }
      } catch (e) {
        notifyError(e)
      }
    }
  }
}
</script>
